import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Strike from '@tiptap/extension-strike';
import Highlight from '@tiptap/extension-highlight';
import Color from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';

// Custom styles for TipTap editor
const editorStyles = `
  .ProseMirror {
    outline: none;
    min-height: 200px;
    padding: 1rem;
    color: inherit;
  }
  
  .ProseMirror p.is-editor-empty:first-child::before {
    color: #adb5bd;
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
  }
  
  .dark .ProseMirror {
    color: #f9fafb;
  }
  
  .dark .ProseMirror p.is-editor-empty:first-child::before {
    color: #6b7280;
  }
  
  .ProseMirror h1,
  .ProseMirror h2,
  .ProseMirror h3,
  .ProseMirror h4,
  .ProseMirror h5,
  .ProseMirror h6 {
    margin-top: 1rem;
    margin-bottom: 0.5rem;
    font-weight: 600;
  }
  
  .ProseMirror h1 { font-size: 1.875rem; }
  .ProseMirror h2 { font-size: 1.5rem; }
  .ProseMirror h3 { font-size: 1.25rem; }
  
  .ProseMirror ul,
  .ProseMirror ol {
    padding-left: 1.5rem;
    margin: 0.5rem 0;
  }
  
  .ProseMirror blockquote {
    border-left: 4px solid #e5e7eb;
    padding-left: 1rem;
    margin: 1rem 0;
    font-style: italic;
  }
  
  .dark .ProseMirror blockquote {
    border-left-color: #4b5563;
  }
  
  .ProseMirror img {
    max-width: 100%;
    height: auto;
    margin: 0.5rem;
    display: inline-block;
    vertical-align: top;
  }
  
  /* Support for side-by-side images */
  .ProseMirror .image-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin: 1rem 0;
  }
  
  .ProseMirror .image-container img {
    flex: 1;
    min-width: 200px;
    max-width: calc(50% - 0.25rem);
  }
  
  /* Support for clickable images */
  .ProseMirror a img {
    cursor: pointer;
    transition: opacity 0.2s ease;
    border: 1px solid transparent;
    border-radius: 4px;
  }
  
  .ProseMirror a img:hover {
    opacity: 0.8;
    border-color: #3b82f6;
  }
  
  .ProseMirror a {
    color: #3b82f6;
    text-decoration: underline;
    display: inline-block;
  }
  
  .dark .ProseMirror a {
    color: #60a5fa;
  }
  
  /* Ensure clickable images are properly styled */
  .ProseMirror a[href] img {
    pointer-events: auto;
    cursor: pointer;
  }
  
  /* Support for side-by-side clickable images */
  .ProseMirror .image-container a {
    display: inline-block;
    flex: 1;
    min-width: 200px;
    max-width: calc(50% - 0.25rem);
  }
`;

// Simple function to preserve clickable images in HTML
const preserveClickableImages = (html) => {
  if (!html) return html;
  
  // Ensure clickable images maintain their structure
  // This is a simple approach that doesn't interfere with TipTap
  return html;
};

const DualEditor = ({ value, onChange, placeholder = "Enter content..." }) => {
  const [editorMode, setEditorMode] = useState('visual'); // 'visual' or 'html'
  const [htmlContent, setHtmlContent] = useState(value || '');

  // Inject custom styles
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = editorStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // TipTap editor configuration - simplified for clickable images
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Keep link extension enabled but configure it properly
        link: {
          openOnClick: true,
          HTMLAttributes: {
            class: 'text-blue-600 underline cursor-pointer',
            target: '_blank',
            rel: 'noopener noreferrer',
          },
        },
        underline: false,
        strike: false,
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: 'max-w-full h-auto',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      Strike,
      Highlight,
      Color,
      TextStyle,
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setHtmlContent(html);
      onChange(html);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[200px] p-4',
        'data-placeholder': placeholder,
      },
    },
  });

  useEffect(() => {
    if (editor && value !== htmlContent) {
      editor.commands.setContent(value || '');
      setHtmlContent(value || '');
    }
  }, [value, editor]);

  const handleHtmlChange = (e) => {
    const newContent = e.target.value;
    setHtmlContent(newContent);
    onChange(newContent);
    if (editor) {
      editor.commands.setContent(newContent);
    }
  };

  const handleModeChange = (mode) => {
    setEditorMode(mode);
  };

  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url && editor) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const addImage = () => {
    const url = window.prompt('Enter image URL:');
    if (url && editor) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  return (
    <div className="space-y-4">
      {/* Editor Mode Toggle */}
      <div className="flex space-x-2">
        <button
          type="button"
          onClick={() => handleModeChange('visual')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            editorMode === 'visual'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          Visual Editor
        </button>
        <button
          type="button"
          onClick={() => handleModeChange('html')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            editorMode === 'html'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          HTML Editor
        </button>
      </div>

      {/* Editor Content */}
      <div className="border border-gray-300 dark:border-gray-600 rounded-md">
        {editorMode === 'visual' ? (
          <div className="bg-white dark:bg-gray-800">
            {/* Toolbar */}
            {editor && (
              <div className="border-b border-gray-300 dark:border-gray-600 p-2 flex flex-wrap gap-1">
                <button
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    editor.isActive('bold') ? 'bg-blue-100 dark:bg-blue-900' : ''
                  }`}
                  title="Bold"
                >
                  <strong>B</strong>
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    editor.isActive('italic') ? 'bg-blue-100 dark:bg-blue-900' : ''
                  }`}
                  title="Italic"
                >
                  <em>I</em>
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    editor.isActive('underline') ? 'bg-blue-100 dark:bg-blue-900' : ''
                  }`}
                  title="Underline"
                >
                  <u>U</u>
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleStrike().run()}
                  className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    editor.isActive('strike') ? 'bg-blue-100 dark:bg-blue-900' : ''
                  }`}
                  title="Strike"
                >
                  <s>S</s>
                </button>
                <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>
                <button
                  onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                  className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    editor.isActive('heading', { level: 1 }) ? 'bg-blue-100 dark:bg-blue-900' : ''
                  }`}
                  title="Heading 1"
                >
                  H1
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                  className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    editor.isActive('heading', { level: 2 }) ? 'bg-blue-100 dark:bg-blue-900' : ''
                  }`}
                  title="Heading 2"
                >
                  H2
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                  className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    editor.isActive('heading', { level: 3 }) ? 'bg-blue-100 dark:bg-blue-900' : ''
                  }`}
                  title="Heading 3"
                >
                  H3
                </button>
                <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>
                <button
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    editor.isActive('bulletList') ? 'bg-blue-100 dark:bg-blue-900' : ''
                  }`}
                  title="Bullet List"
                >
                  •
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    editor.isActive('orderedList') ? 'bg-blue-100 dark:bg-blue-900' : ''
                  }`}
                  title="Numbered List"
                >
                  1.
                </button>
                <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>
                <button
                  onClick={() => editor.chain().focus().setTextAlign('left').run()}
                  className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    editor.isActive({ textAlign: 'left' }) ? 'bg-blue-100 dark:bg-blue-900' : ''
                  }`}
                  title="Align Left"
                >
                  ←
                </button>
                <button
                  onClick={() => editor.chain().focus().setTextAlign('center').run()}
                  className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    editor.isActive({ textAlign: 'center' }) ? 'bg-blue-100 dark:bg-blue-900' : ''
                  }`}
                  title="Align Center"
                >
                  ↔
                </button>
                <button
                  onClick={() => editor.chain().focus().setTextAlign('right').run()}
                  className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                    editor.isActive({ textAlign: 'right' }) ? 'bg-blue-100 dark:bg-blue-900' : ''
                  }`}
                  title="Align Right"
                >
                  →
                </button>
                <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>
                <button
                  onClick={addLink}
                  className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                  title="Add Link"
                >
                  🔗
                </button>
                <button
                  onClick={addImage}
                  className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                  title="Add Image"
                >
                  🖼️
                </button>
                <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>
                <button
                  onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
                  className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                  title="Clear Formatting"
                >
                  🧹
                </button>
              </div>
            )}
            {/* Editor */}
            <EditorContent editor={editor} className="min-h-[200px]" />
          </div>
        ) : (
          <div className="p-4">
            <textarea
              value={htmlContent}
              onChange={handleHtmlChange}
              placeholder={placeholder}
              rows="12"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white font-mono text-sm"
            />
          </div>
        )}
      </div>

      {/* Preview Toggle (for HTML mode) */}
      {editorMode === 'html' && (
        <div className="mt-4">
          <details className="border border-gray-300 dark:border-gray-600 rounded-md">
            <summary className="px-4 py-2 bg-gray-50 dark:bg-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 font-medium">
              Preview HTML Content
            </summary>
            <div className="p-4 bg-white dark:bg-gray-800 border-t border-gray-300 dark:border-gray-600">
              <div 
                className="prose prose-sm max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
            </div>
          </details>
        </div>
      )}
    </div>
  );
};

export default DualEditor; 